// Adapted from https://beui.dev/r/image-viewer; MIT notice in components/beui/LICENSE.txt.
"use client";
// beui.dev/components/motion/image-viewer

import { ChevronLeft, ChevronRight, Minus, Plus, X } from "lucide-react";
import {
  type HTMLMotionProps,
  type Variants,
  AnimatePresence,
  LayoutGroup,
  motion,
  useMotionValue,
  useIsPresent,
  useReducedMotion,
} from "motion/react";
import {
  type ComponentPropsWithRef,
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  EASE_DRAWER,
  EASE_OUT,
  SPRING_LAYOUT,
  SPRING_PRESS,
} from "../../lib/beui/ease";
import { useModalScope } from "../../lib/beui/useModalScope";
import { PresenceGate } from "../../lib/beui/PresenceGate";
import { cn } from "../../lib/beui/utils";

import { LightboxFrame, controlClass } from "./ImageViewerFrame";

export interface LightboxImage {
  id: string;
  src: string;
  alt: string;
  /** Supply both intrinsic dimensions for thumbnail morphs, or omit both to measure only the selected photo. */
  width?: number;
  height?: number;
  caption?: string;
}

export interface MorphingLightboxProps {
  images: LightboxImage[];
  value?: string | null;
  defaultValue?: string | null;
  onValueChange?: (id: string | null) => void;
  label?: string;
  className?: string;
  thumbnailClassName?: string;
  renderCaption?: (image: LightboxImage) => ReactNode;
  /** Compose gallery and viewer parts instead of the default layout. */
  children?: ReactNode;
}

export type ImageViewerImage = LightboxImage;
export type ImageViewerProps = MorphingLightboxProps;

type ViewerContextValue = {
  images: LightboxImage[];
  value: string | null;
  image: LightboxImage | undefined;
  index: number;
  label: string;
  groupId: string;
  reduce: boolean;
  renderCaption?: (image: LightboxImage) => ReactNode;
  select: (id: string | null) => void;
  move: (direction: -1 | 1) => void;
};
const ViewerContext = createContext<ViewerContextValue | null>(null);

function useViewerContext(part: string) {
  const context = useContext(ViewerContext);
  if (!context) throw new Error(`${part} must be used within ImageViewer.`);
  return context;
}

/** Shared selection and navigation for custom gallery and viewer controls. */
export function useImageViewer() {
  const { images, value, image, index, select, move } =
    useViewerContext("useImageViewer");
  return {
    images,
    value,
    image,
    index,
    hasPrevious: index > 0,
    hasNext: index >= 0 && index < images.length - 1,
    select,
    close: () => select(null),
    previous: () => move(-1),
    next: () => move(1),
  };
}

/** A composable image gallery with a focus-managed, thumbnail-connected viewer. */
export function ImageViewer({
  images,
  value: controlledValue,
  defaultValue = null,
  onValueChange,
  label = "Image gallery",
  className,
  thumbnailClassName,
  renderCaption,
  children,
}: ImageViewerProps) {
  const [internalValue, setInternalValue] = useState(defaultValue);
  const value = controlledValue === undefined ? internalValue : controlledValue;
  const index = images.findIndex((image) => image.id === value);
  const image = images[index];
  const groupId = useId();
  const reduce = useReducedMotion() ?? false;
  if (new Set(images.map((item) => item.id)).size !== images.length)
    throw new Error("ImageViewer requires unique image ids.");
  if (
    images.some(
      (item) =>
        (item.width !== undefined || item.height !== undefined) && (
          typeof item.width !== "number" || typeof item.height !== "number" ||
          !Number.isFinite(item.width) || !Number.isFinite(item.height) ||
          item.width <= 0 || item.height <= 0
        ),
    )
  )
    throw new Error("ImageViewer requires positive image dimensions.");
  const select = (id: string | null) => {
    if (controlledValue === undefined) setInternalValue(id);
    onValueChange?.(id);
  };
  // Drop removed identities instead of letting a re-added image reopen a session.
  if (controlledValue === undefined && internalValue !== null && index < 0)
    setInternalValue(null);
  const move = (direction: -1 | 1) => {
    const next = images[index + direction];
    if (image && next) select(next.id);
  };
  return (
    <ViewerContext.Provider
      value={{
        images,
        value,
        image,
        index,
        label,
        groupId,
        reduce,
        renderCaption,
        select,
        move,
      }}
    >
      <LayoutGroup id={groupId}>
        {children === undefined ? (
          <>
            <ImageViewerGallery className={className}>
              {images.map((item) => (
                <ImageViewerThumbnail
                  key={item.id}
                  imageId={item.id}
                  className={thumbnailClassName}
                />
              ))}
            </ImageViewerGallery>
            <ImageViewerContent />
          </>
        ) : (
          children
        )}
      </LayoutGroup>
    </ViewerContext.Provider>
  );
}

/** Generic name; the original export remains compatible with existing installs. */
export { ImageViewer as MorphingLightbox };

export function ImageViewerGallery({
  className,
  ...props
}: ComponentPropsWithRef<"section">) {
  const { label } = useViewerContext("ImageViewerGallery");
  return (
    <section
      aria-label={label}
      {...props}
      className={cn("grid w-full grid-cols-2 gap-3 sm:grid-cols-3", className)}
    />
  );
}

export interface ImageViewerThumbnailProps
  extends Omit<HTMLMotionProps<"button">, "children"> {
  imageId: string;
  imageClassName?: string;
  children?: ReactNode;
}

/** The image keeps its shared-layout identity when the trigger is customised. */
export function ImageViewerThumbnail({
  imageId,
  className,
  imageClassName,
  children,
  onClick,
  disabled,
  ...props
}: ImageViewerThumbnailProps) {
  const { images, value, groupId, reduce, select } = useViewerContext(
    "ImageViewerThumbnail",
  );
  const image = images.find((item) => item.id === imageId);
  if (!image)
    throw new Error(`ImageViewerThumbnail: unknown image id "${imageId}".`);
  return (
    <motion.button
      type="button"
      aria-label={`Open ${image.alt}`}
      whileTap={reduce ? undefined : { scale: 0.98 }}
      transition={SPRING_PRESS}
      {...props}
      disabled={disabled}
      aria-haspopup="dialog"
      aria-expanded={image.id === value}
      onClick={(event) => {
        onClick?.(event);
        if (!event.defaultPrevented && !disabled) select(image.id);
      }}
      className={cn(
        "relative overflow-hidden rounded-2xl bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        className,
      )}
    >
      {/* biome-ignore lint/performance/noImgElement: Copy-paste registry images must work outside Next.js. */}
      <motion.img
        layoutId={reduce ? undefined : `${groupId}-${image.id}`}
        src={image.src}
        alt={image.alt}
        width={image.width}
        height={image.height}
        loading="lazy"
        draggable={false}
        transition={SPRING_LAYOUT}
        className={cn("block h-auto w-full", imageClassName)}
      />
      {children}
    </motion.button>
  );
}

export function ImageViewerCounter({
  className,
  ...props
}: ComponentPropsWithRef<"span">) {
  const { index, images } = useViewerContext("ImageViewerCounter");
  return (
    <span
      aria-live="polite"
      {...props}
      className={cn("text-xs tabular-nums", className)}
    >
      {index + 1} / {images.length}
    </span>
  );
}

export function ImageViewerCaption({
  className,
  children,
  ...props
}: ComponentPropsWithRef<"div">) {
  const { image, renderCaption } = useViewerContext("ImageViewerCaption");
  return (
    <div
      aria-live="polite"
      {...props}
      data-lightbox-content=""
      className={cn("min-w-0 text-center text-sm", className)}
    >
      {children ??
        (image &&
          (renderCaption
            ? renderCaption(image)
            : (image.caption ?? image.alt)))}
    </div>
  );
}

type ViewerButtonProps = ComponentPropsWithRef<"button">;
function ViewerButton({
  action,
  className,
  children,
  disabled,
  onClick,
  ...props
}: ViewerButtonProps & { action: "close" | "previous" | "next" }) {
  const { index, images, select, move } = useViewerContext(
    "ImageViewer controls",
  );
  const unavailable =
    action === "previous"
      ? index <= 0
      : action === "next"
        ? index < 0 || index >= images.length - 1
        : false;
  const Icon =
    action === "close" ? X : action === "previous" ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      aria-label={
        action === "close"
          ? "Close viewer"
          : action === "previous"
            ? "Previous image"
            : "Next image"
      }
      {...props}
      disabled={disabled || unavailable}
      onClick={(event) => {
        onClick?.(event);
        if (!event.defaultPrevented) {
          if (action === "close") select(null);
          else move(action === "previous" ? -1 : 1);
        }
      }}
      className={cn(controlClass, className)}
    >
      {children ?? <Icon size={18} aria-hidden="true" />}
    </button>
  );
}
export function ImageViewerClose(props: ViewerButtonProps) {
  return <ViewerButton {...props} action="close" />;
}
export function ImageViewerPrevious(props: ViewerButtonProps) {
  return <ViewerButton {...props} action="previous" />;
}
export function ImageViewerNext(props: ViewerButtonProps) {
  return <ViewerButton {...props} action="next" />;
}

export interface ImageViewerContentProps {
  className?: string;
  /** Custom header; defaults to the counter and close control. */
  header?: ReactNode;
  /** Custom footer; defaults to navigation and the image caption. */
  children?: ReactNode;
}

/** Owns the portal, focus scope, swipe/zoom frame and exit interaction gate. */
export function ImageViewerContent({
  className,
  header,
  children,
}: ImageViewerContentProps) {
  const context = useViewerContext("ImageViewerContent");
  const { image, index, label, groupId, reduce, select, move } = context;
  const [mounted, setMounted] = useState(false);
  const portal = useRef<HTMLDivElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    setMounted(true);
  }, []);
  useModalScope(mounted && Boolean(image), portal, panel, () => select(null));
  const selectedId = image?.id;
  const previousId = useRef<string | undefined>(undefined);
  useLayoutEffect(() => {
    const previous = previousId.current;
    previousId.current = mounted ? selectedId : undefined;
    if (!mounted || !selectedId || !previous || !panel.current) return;
    const focused = document.activeElement;
    if (
      !panel.current.contains(focused) ||
      (focused instanceof HTMLButtonElement && focused.disabled)
    ) {
      (
        panel.current.querySelector<HTMLButtonElement>(
          "button:not(:disabled)",
        ) ?? panel.current
      ).focus({ preventScroll: true });
    }
  }, [mounted, selectedId]);
  if (!mounted) return null;
  return createPortal(
    <div ref={portal}>
      <AnimatePresence>
        {image && (
          <PresenceGate>
            {({ isPresent, gate }) => (
              <ViewerContext.Provider value={context}>
                <motion.button
                  {...gate}
                  type="button"
                  tabIndex={-1}
                  aria-hidden="true"
                  onClick={() => select(null)}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: reduce ? 0.1 : 0.18, ease: EASE_OUT }}
                  className="fixed inset-0 z-[100] h-full w-full cursor-default bg-black/90"
                />
                <div
                  {...(!isPresent ? { inert: "" } : {})}
                  className="pointer-events-none fixed inset-3 z-[101] sm:inset-6"
                >
                  <div
                    {...gate}
                    ref={panel}
                    role="dialog"
                    aria-modal="true"
                    aria-label={label}
                    tabIndex={-1}
                    className={cn(
                      "flex h-full flex-col outline-none",
                      className,
                    )}
                    onPointerDownCapture={(event) => {
                      const target = event.target;
                      if (
                        target instanceof Element &&
                        !target.closest(
                          "button, a, input, select, textarea, [data-lightbox-content]",
                        )
                      ) {
                        event.preventDefault();
                        select(null);
                      }
                    }}
                    onKeyDown={(event) => {
                      if (
                        (event.target instanceof Element &&
                          event.target.closest(
                            'input, textarea, select, [contenteditable="true"]',
                          )) ||
                        event.defaultPrevented ||
                        event.altKey ||
                        event.ctrlKey ||
                        event.metaKey ||
                        event.shiftKey
                      )
                        return;
                      if (
                        event.key === "ArrowLeft" ||
                        event.key === "ArrowRight"
                      ) {
                        event.preventDefault();
                        move(event.key === "ArrowLeft" ? -1 : 1);
                      }
                    }}
                  >
                    <div
                      data-lightbox-content=""
                      className="flex items-center justify-between gap-3 pb-3 text-white"
                    >
                      {header === undefined ? (
                        <>
                          <ImageViewerCounter />
                          <ImageViewerClose />
                        </>
                      ) : (
                        header
                      )}
                    </div>
                    <LightboxFrame
                      image={image}
                      index={index}
                      layoutId={reduce ? undefined : `${groupId}-${image.id}`}
                      reduce={reduce}
                      onSwipe={move}
                    />
                    <div
                      data-lightbox-content=""
                      className="flex items-center justify-between gap-3 pt-3 text-white"
                    >
                      {children === undefined ? (
                        <>
                          <ImageViewerPrevious />
                          <ImageViewerCaption />
                          <ImageViewerNext />
                        </>
                      ) : (
                        children
                      )}
                    </div>
                  </div>
                </div>
              </ViewerContext.Provider>
            )}
          </PresenceGate>
        )}
      </AnimatePresence>
    </div>,
    document.body,
  );
}
